/**
 * BooleanField — a `boolean` attribute rendered as a tri-state control (PIM
 * Wave P2, Task 3): Unanswered / Yes / No, encoded `BOOL_UNANSWERED` /
 * `BOOL_TRUE` / `BOOL_FALSE`. The explicit "unanswered" segment is a genuine
 * third state, mirroring the backend "no stored value row = unanswered" law
 * (so an untouched boolean never counts as "answered false" for completeness
 * or as a matched controller for visibility) — the same choice the admin
 * `FormPreview` made. `Segmented` (from `@garden/ui`) is the control.
 */
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Segmented } from "@garden/ui";

import { SELLER_DASHBOARD_NS } from "@/features/seller-dashboard/api";
import {
  BOOL_FALSE,
  BOOL_TRUE,
  BOOL_UNANSWERED,
  pickOptionalText,
  pickText,
} from "@/features/seller-dashboard/attributes/formSchema";
import {
  FieldShell,
  type AttributeFieldProps,
} from "@/features/seller-dashboard/attributes/fields/FieldShell";

export function BooleanField({
  field,
  language,
  value,
  onChange,
  error,
}: AttributeFieldProps): ReactElement {
  const { t } = useTranslation(SELLER_DASHBOARD_NS);
  const label = pickText(field.label, language);
  const hint = pickOptionalText(field.hint, language);
  const segValue = value === BOOL_TRUE || value === BOOL_FALSE ? value : BOOL_UNANSWERED;
  return (
    <FieldShell
      slug={field.field_slug}
      label={label}
      requirement={field.requirement}
      hint={hint}
      error={error}
    >
      <div role="group" aria-label={label} data-testid={`attr-widget-${field.field_slug}`}>
        <Segmented
          options={[
            { value: BOOL_UNANSWERED, label: t("attributes.boolean.unanswered") },
            { value: BOOL_TRUE, label: t("attributes.boolean.yes") },
            { value: BOOL_FALSE, label: t("attributes.boolean.no") },
          ]}
          value={segValue}
          onChange={(next) => onChange(next)}
        />
      </div>
    </FieldShell>
  );
}

export default BooleanField;
